export type UUID = string;
export type Email = string;
export type ISODate = string;
export type ISODateTime = string;

export type Str = string | null;
export type Num = number | null;
export type NumLike = number | string | null;
export type Bool = boolean | null;
export type Arr<T> = T[] | null;

export interface B2BProfile {
  pixeling_experience: Num;
  website_conversion_experience: Num;
  average_order_value: Num;
  brand_recognition: Num;
  estimated_time_to_conversion: Num;
  estimated_audience_size: NumLike;
}

export interface B2CProfile {
  pixeling_experience: Num;
  website_conversion_experience: Num;
  average_order_value: Num;
  brand_recognition: Num;
  estimated_time_to_conversion: Num;
  estimated_audience_size: Num;
}

export interface FullAdvertiserProfile extends AdvertiserProfile {
  id: UUID;
}

export interface AdvertiserProfile {
  name: Str;
  agency_id: Str;
  industry: Str;
  sub_industry: Str;
  url: Str;
  b2b: B2BProfile | null;
  b2c: B2CProfile | null;
}

export interface ChannelSelection {
  channel_name: Str;
  flights?: Arr<{
    start_date: ISODate | null;
    end_date: ISODate | null;
    percentage?: Num;
    amount?: Num;
    flight_number?: number | null;
  }>;
  flight_number?: number | null;
  start_date?: ISODate | null;
  end_date?: ISODate | null;
  percentage?: Num;
  amount?: Num;
}

export interface CampaignKPI {
  kpi_name: Str;
  percentage: Num;
  amount: Num;
  kpi_goal: Str;
  kpi_goal_amount: Num;
  channel_selections: Arr<ChannelSelection>;
  channel_exclusions: Arr<string>;
  skip_channel_prediction: Bool;
}

export type ProjectedKPI = CampaignKPI;

export interface OutputRecord {
  id?: UUID;
  business_goal?: Str;
  campaign_id?: Str;
  plan?: Str;
  media_type?: Str;
  channel?: Str;
  campaign_kpi?: Str;
  scenario_id?: UUID;
  start_date?: ISODate | null;
  end_date?: ISODate | null;
  flight_budget?: Num;
  flight_percentage?: Num;
  [metric: string]: string | number | null | undefined;
}
export interface ScenarioOutput {
  outdated: Bool;
  data: Arr<OutputRecord>;
  original_data?: Arr<OutputRecord>;
  timestamp: Num;
  status_summary: Arr<string>;
  projected_kpis: Arr<ProjectedKPI>;
  budget_in_time: Arr<BudgetInTimeItem>;
  kpis_with_no_sample: Arr<string>;
  grp_threshold_alerts: GrpThresholdAlerts;
}

export interface Scenario {
  name: Str;
  id: UUID;

  gender: Str;
  age_group: Str;
  states: Arr<string>;
  cities: Arr<string>;
  dma: Arr<string>;
  zip_codes: Arr<string>;

  estimated_audience_size: Num;
  start_date: ISODate | null;
  end_date: ISODate | null;
  flight_cadence?: Str;
  net_budget: Num;
  gross_budget: Num;
  campaign_kpis: Arr<CampaignKPI>;
  kpi_goal_enabled: boolean;
  owned_by: Str;
  owner: Email | null;
  approved: Bool;
  output: ScenarioOutput | null;
}

export interface Plan {
  name: Str;
  id: UUID;
  owned_by: Str;
  owner: Email | null;
  approved: Bool;
  scenarios: Arr<Scenario>;
}

export interface BusinessGoal {
  name: Str;
  id: UUID;
  campaign_id: Str;
  business_goal: Str;
  b2b_or_b2c: Str;
  owned_by: Str;
  owner: Email | null;
  plans: Arr<Plan>;
}

export interface Project {
  name: string;
  id: UUID;
  agency_name: Str;
  agency_id: Str;
  advertiser_id: UUID | null;
  advertiser_profile: AdvertiserProfile | null;

  owned_by: Str;
  owner: Email | null;

  editors: Arr<Email>;
  viewers: Arr<Email>;

  publish_date: ISODateTime | null;
  last_modified: ISODateTime | null;

  business_goals: Arr<BusinessGoal>;
}

export type ScenarioDateInfo = {
  scenario_id: string;
  start_date: string;
  end_date: string;
};

export type ScenarioFlightingContext = {
  scenario_id: string;
  gender: string;
  age_group: string;
  dma_names: string[];
  advertiser_name: string;
  profile_type: string;
  brand_recognition: number;
  estimated_time_to_conversion: number;
};

export type MetricsRowInput = {
  id?: string;
  flight_budget?: number;
  flight_percentage?: number;
  start_date?: string;
  end_date?: string;
  run_flighting?: boolean;
  [k: string]: string | number | boolean | null | undefined;
};

export type RecalculatePlanInput = {
  scenario_dates: ScenarioDateInfo[];
  scenario_flighting_contexts: ScenarioFlightingContext[];
  metrics_rows: MetricsRowInput[];
};

export type StatusSummaryEntry = {
  scenario_id: string;
  messages: string[];
};

export type RecalculatePlanOutput = {
  metrics_rows: MetricsRowInput[];
  budget_in_time: BudgetInTimeItem[];
  status_summary: StatusSummaryEntry[];
};

export type BudgetInTimeItem = {
  scenario_id?: string;
  kpi: string;
  channel: string;
  distribution: BudgetTimeEntry[];
};

export type BudgetTimeEntry = {
  start_date: string;
  end_date: string;
  daily_budget: number;
};

export type GrpThresholdEntry = {
  start_date: string;
  end_date: string;
};

export type GrpThresholdAlerts = Record<string, Record<string, Arr<GrpThresholdEntry>>>;

// Project Output Table Types
export type NumericKey =
  | "channel_budget"
  | "channel_percentage"
  | "flight_budget"
  | "flight_percentage"
  | "cpm"
  | "impressions"
  | "cplv"
  | "landing_page_views"
  | "cpp"
  | "purchases"
  | "roas"
  | "cpa"
  | "cpc"
  | "ctr"
  | "clicks"
  | "cpl"
  | "cpcv"
  | "cpi"
  | "cpe"
  | "cpae"
  | "cpb"
  | "cper"
  | "cpia"
  | "cplc"
  | "cppl"
  | "cpsa"
  | "cpsv"
  | "cps"
  | "cpla"
  | "leads"
  | "acquisitions"
  | "app_installs"
  | "completed_views"
  | "engagements"
  | "purchase_value"
  | "app_events"
  | "bookings"
  | "event_response"
  | "intent_actions"
  | "link_clicks"
  | "page_likes"
  | "site_action"
  | "store_visits"
  | "submission_entries"
  | "video_views"
  | "local_action"
  | "vcr";

export const NUMERIC_KEYS = new Set<NumericKey>([
  "channel_budget",
  "channel_percentage",
  "flight_budget",
  "flight_percentage",
  "cpm",
  "impressions",
  "cplv",
  "landing_page_views",
  "cpp",
  "purchases",
  "roas",
  "cpa",
  "cpc",
  "ctr",
  "clicks",
  "cpl",
  "cpcv",
  "cpi",
  "cpe",
  "cpae",
  "cpb",
  "cper",
  "cpia",
  "cplc",
  "cppl",
  "cpsa",
  "cpsv",
  "cps",
  "cpla",
  "leads",
  "acquisitions",
  "app_installs",
  "completed_views",
  "engagements",
  "purchase_value",
  "app_events",
  "bookings",
  "event_response",
  "intent_actions",
  "link_clicks",
  "page_likes",
  "site_action",
  "store_visits",
  "submission_entries",
  "video_views",
  "local_action",
  "vcr",
]);

export type GenerateOutputRequestApi = {
  jobId: string;
};
